import * as React from 'react';
import { Box, Typography, makeStyles, Theme, createStyles } from '@material-ui/core';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import AccountBalanceWalletOutlinedIcon from '@material-ui/icons/AccountBalanceWalletOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import EventAvailableOutlinedIcon from '@material-ui/icons/EventAvailableOutlined';
import ListAltOutlinedIcon from '@material-ui/icons/ListAltOutlined';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import { SvgIconTypeMap } from '@material-ui/core/SvgIcon';
import { OverridableComponent } from '@material-ui/core/OverridableComponent';
import { browserHistory } from '../components/AppContent';

// Explicit request: a plain (not timeline-style) vertical tab list for the Matter Details
// page's own section tabs, a bit larger than a typical tab row with a short subtitle under
// each label - replacing the old Semantic UI RvTab (components/Tab.tsx) this page used.
// RvTab itself is left untouched - it's still used by ClientAndContactDetails.tsx and
// Print.tsx, well outside this page's own scope.
//
// Colours come from this app's own --rv-accent-* tokens (App.css) - the same palette
// EnquiryFormDrawer.tsx's Details/Activity tabs and HistoryActivity.tsx's timeline already
// use - rather than a fresh set of hex constants local to just this page.
const TAB_META_BY_ROUTE: { [routeName: string]: { icon: OverridableComponent<SvgIconTypeMap>; subtitle: string; bg: string; color: string } } = {
    staff: { icon: PeopleOutlineIcon, subtitle: "Who's responsible for this matter", bg: 'var(--rv-accent-indigo-bg)', color: 'var(--rv-accent-indigo)' },
    generalInformation: { icon: InfoOutlinedIcon, subtitle: 'Category, type, and key details', bg: 'var(--rv-accent-blue-bg)', color: 'var(--rv-accent-blue)' },
    financials: { icon: AccountBalanceWalletOutlinedIcon, subtitle: 'Costs, billing, and trust balances', bg: 'var(--rv-accent-green-bg)', color: 'var(--rv-accent-green)' },
    documents: { icon: DescriptionOutlinedIcon, subtitle: 'Files attached to this matter', bg: 'var(--rv-accent-grey-bg)', color: 'var(--rv-accent-grey)' },
    messages: { icon: ChatBubbleOutlineIcon, subtitle: 'Notes and message history', bg: 'var(--rv-accent-purple-bg)', color: 'var(--rv-accent-purple)' },
    tasks: { icon: AssignmentOutlinedIcon, subtitle: 'Tasks linked to this matter', bg: 'var(--rv-accent-orange-bg)', color: 'var(--rv-accent-orange)' },
    timeline: { icon: EventAvailableOutlinedIcon, subtitle: 'Chronological history of activity', bg: 'var(--rv-accent-teal-bg)', color: 'var(--rv-accent-teal)' },
    otherInformation: { icon: ListAltOutlinedIcon, subtitle: 'Additional matter-specific details', bg: 'var(--rv-accent-grey-bg)', color: 'var(--rv-accent-grey)' },
    parties: { icon: PersonOutlineIcon, subtitle: 'People and organisations involved', bg: 'var(--rv-accent-indigo-bg)', color: 'var(--rv-accent-indigo)' },
};
// Fallback for any route this map doesn't know about, so a new tab added later doesn't
// crash - just renders with a neutral chip and no subtitle instead of a matched one.
const DEFAULT_TAB_META = { icon: InfoOutlinedIcon, subtitle: '', bg: 'var(--rv-accent-grey-bg)', color: 'var(--rv-accent-grey)' };

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        // Explicit request: dock the tab rail flush against the page's left/top edges (like
        // /emailConfiguration's FilteredVerticalTabPage sidebar) instead of floating as a
        // rounded card — RecordDetailLayout's `flushBody` strips its own padding for this, so
        // `main` below now owns the content-area padding that used to live on the parent.
        wrap: {
            display: 'flex',
            alignItems: 'stretch',
            // RecordDetailLayout's `body` is now a flex column, so this claims the real
            // bounded height it offers instead of collapsing to content size — needed so a
            // tab that wants to fill the available height (e.g. the Tasks tab's ag-grid)
            // actually gets one, rather than being compressed to near-zero.
            flex: 1,
            minHeight: 0,
            [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
            },
        },
        side: {
            width: 280,
            flexShrink: 0,
            backgroundColor: '#fff',
            borderRight: `1px solid ${theme.palette.divider}`,
            // Gives the docked rail a bit of depth against the content next to it — same
            // reasoning as the banner's own shadow needing position+zIndex to paint over the
            // normal-flow sibling next to it, rather than being hidden behind it.
            boxShadow: '2px 0 8px rgba(0,0,0,0.10)',
            position: 'relative',
            zIndex: 1,
            overflowY: 'auto',
            [theme.breakpoints.down('sm')]: {
                width: '100%',
                borderRight: 'none',
                borderBottom: `1px solid ${theme.palette.divider}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
            },
        },
        tabList: {
            display: 'flex',
            flexDirection: 'column',
            padding: theme.spacing(1, 0),
        },
        tabButton: {
            display: 'flex',
            alignItems: 'flex-start',
            gap: theme.spacing(1.5),
            width: '100%',
            textAlign: 'left',
            border: 'none',
            background: 'none',
            padding: theme.spacing(1.75, 2),
            cursor: 'pointer',
            borderLeft: '4px solid transparent',
            transition: 'background-color 0.12s, border-color 0.12s',
            '&:hover': {
                backgroundColor: theme.palette.grey[50],
            },
        },
        tabButtonActive: {
            borderLeft: `4px solid var(--rv-accent-blue)`,
            backgroundColor: 'var(--rv-accent-blue-bg)',
        },
        tabIconWrap: {
            width: 40,
            height: 40,
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        tabText: {
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            paddingTop: 2,
        },
        tabLabel: {
            fontSize: 14.5,
            fontWeight: 700,
            color: theme.palette.text.primary,
            lineHeight: 1.3,
        },
        tabLabelActive: {
            color: 'var(--rv-accent-blue)',
        },
        tabSubtitle: {
            fontSize: 12,
            color: theme.palette.text.secondary,
            lineHeight: 1.4,
            marginTop: 2,
        },
        main: {
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            // 20px matches PageContentLayout.tsx's own content Box (padding="20px 20px") — the
            // shared body-padding convention across page shells, see CLAUDE.md's "Body Section
            // Padding" rule. Was theme.spacing(3) (24px).
            padding: 20,
            [theme.breakpoints.down('xs')]: {
                padding: theme.spacing(2),
            },
        },
        // Explicit request: match /applicationSettings/LabelSettings' own selected-tab content
        // box (FilteredVerticalTabPage.tsx's contentCard) for every Matter Details tab, not
        // just Tasks (confirmed there first) — same border/radius/shadow/padding values, and
        // its own overflowY so a tab taller than the viewport scrolls inside the card rather
        // than being clipped (unlike the shared `rv-grid-container` class, which is meant for
        // ag-grids that manage their own internal scroll and clips everything else via
        // overflow:hidden).
        contentBox: {
            // flex:1 fills main's available space; minHeight:0 (not the flex default of
            // "auto") is what actually lets it do that — without it, a child relying on a
            // percentage height (e.g. the Tasks tab's embedded page, itself `height:'100%'`)
            // hits a circular sizing dependency and collapses instead, the same compression
            // bug already fixed once for the Tasks tab alone.
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            borderRadius: 8,
            boxShadow: '0 1px 6px rgba(0,0,0,0.14)',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            padding: 15,
        },
    })
);

interface MatterDetailsTab {
    tabName: string;
    content: React.ReactNode;
    routeName: string;
}

interface MatterDetailsTabListProps {
    tabs: MatterDetailsTab[];
    activeIndex: number;
    matterID: string;
}

export const MatterDetailsTabList: React.FC<MatterDetailsTabListProps> = (props) => {
    const classes = useStyles();

    const activeTab = props.tabs[props.activeIndex] || props.tabs[0];

    const onTabClick = (tab: MatterDetailsTab) => () => {
        // replace, not push — switching tabs is still "this page", so it shouldn't add its own
        // entry to the browser history stack. Explicit request: the header's back arrow must
        // leave Matter Details entirely, not step back through previously-viewed tabs first.
        browserHistory.replace(`/matterDetail/${props.matterID}/${tab.routeName}`);
    };

    return (
        <Box className={classes.wrap}>
            <Box className={classes.side}>
                <Box className={classes.tabList}>
                    {props.tabs.map((tab, index) => {
                        const isActive = index === props.activeIndex;
                        const { icon: TabIcon, subtitle, bg, color } = TAB_META_BY_ROUTE[tab.routeName] || DEFAULT_TAB_META;
                        return (
                            <button
                                key={tab.routeName}
                                type="button"
                                className={`${classes.tabButton} ${isActive ? classes.tabButtonActive : ''}`}
                                onClick={onTabClick(tab)}
                            >
                                <span className={classes.tabIconWrap} style={{ backgroundColor: bg, color: color }}>
                                    <TabIcon fontSize="small" />
                                </span>
                                <span className={classes.tabText}>
                                    <Typography className={`${classes.tabLabel} ${isActive ? classes.tabLabelActive : ''}`}>
                                        {tab.tabName}
                                    </Typography>
                                    {!!subtitle && (
                                        <Typography className={classes.tabSubtitle}>{subtitle}</Typography>
                                    )}
                                </span>
                            </button>
                        );
                    })}
                </Box>
            </Box>
            <Box className={classes.main}>
                <Box className={classes.contentBox}>
                    {activeTab.content}
                </Box>
            </Box>
        </Box>
    );
};
